import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { eventTimeLabel, googleCalendarUrl, parseEventDate, type EventDate } from "../helpers";
import { Icon } from "../icons";
import { CARD_HOVER, Container, MultiLine, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type Item = SectionProps<"events">["node"]["content"]["items"][number];

const actionLink =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-site-button px-4 text-sm font-semibold transition motion-reduce:transition-none";

function DateBlock({ date, large }: { date: EventDate; large?: boolean }) {
  return (
    <time dateTime={`${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`} className={cx("flex flex-col items-center justify-center rounded-site-md bg-site-primary text-center text-site-on-primary shadow-site-sm", large ? "min-w-28 px-5 py-5" : "min-w-20 px-3 py-3")}>
      <span className="text-[0.6875rem] font-semibold tracking-[0.18em] uppercase opacity-85">{date.weekday}</span>
      <span className={cx("baze-heading leading-none tabular-nums", large ? "my-1.5 text-5xl" : "my-1 text-4xl")}>{date.day}</span>
      <span className="text-xs font-semibold tracking-wide">
        {date.monthName} {date.year}
      </span>
    </time>
  );
}

function Details({ ctx, item, i }: { ctx: RenderContext; item: Item; i: number }) {
  const p = `content.items.${i}`;
  const time = eventTimeLabel(item);
  if (!time && !item.venue && !item.address) return null;
  return (
    <ul className="space-y-2.5 text-[0.9375rem] text-site-muted">
      {time ? (
        <li className="flex gap-3">
          <Icon name="clock" className="mt-0.5 size-[1.125rem] shrink-0 text-site-primary" />
          <span className="tabular-nums" {...field(ctx, `${p}.startTime`)}>
            {time}
          </span>
        </li>
      ) : null}
      {item.venue || item.address ? (
        <li className="flex gap-3">
          <Icon name="map-pin" className="mt-0.5 size-[1.125rem] shrink-0 text-site-primary" />
          <span>
            {item.venue ? (
              <span className="block font-semibold text-site-text" {...field(ctx, `${p}.venue`)}>
                {item.venue}
              </span>
            ) : null}
            {item.address ? <span {...field(ctx, `${p}.address`)}>{item.address}</span> : null}
          </span>
        </li>
      ) : null}
    </ul>
  );
}

function Actions({ ctx, item, showCalendar }: { ctx: RenderContext; item: Item; showCalendar: boolean }) {
  const calendar = showCalendar ? googleCalendarUrl(item, ctx.site.name) : null;
  if (!item.mapUrl && !calendar) return null;
  return (
    <div className="mt-6 flex flex-wrap gap-2.5">
      {item.mapUrl ? (
        <SiteLink ctx={ctx} href={item.mapUrl} fallback="none" className={cx(actionLink, "bg-site-primary text-site-on-primary shadow-site-sm hover:shadow-site-md")} ariaLabel={`Buka lokasi ${item.name} di peta`}>
          <Icon name="navigation" className="size-4" />
          <span>Lihat lokasi</span>
        </SiteLink>
      ) : null}
      {calendar ? (
        <SiteLink ctx={ctx} href={calendar} fallback="none" className={cx(actionLink, "border border-site-border text-site-text hover:border-site-primary hover:text-site-primary")} ariaLabel={`Simpan ${item.name} ke Google Calendar`}>
          <Icon name="calendar" className="size-4" />
          <span>Simpan ke kalender</span>
        </SiteLink>
      ) : null}
    </div>
  );
}

/** Dated happenings (akad, resepsi, workshop, open house) with venue, map and calendar links. */
export function Events({ node, ctx }: SectionProps<"events">) {
  const c = node.content;
  const items = c.items;
  const showCalendar = c.showCalendar !== false;

  // Ledger rows (a venue's agenda): date, what happens, where and when, separated by hairlines.
  if (node.variant === "agenda") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
          <ul className={cx(STACK, "border-b border-site-border")}>
            {items.map((item, i) => {
              const date = parseEventDate(item.date);
              return (
                <li key={i} className="baze-reveal grid gap-5 border-t border-site-border py-7 @3xl:grid-cols-[7rem_1fr_16rem] @3xl:items-start @3xl:gap-8 @3xl:py-9">
                  {date ? (
                    <time dateTime={`${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`} className="flex items-baseline gap-3 text-site-text @3xl:flex-col @3xl:gap-1">
                      <span className="text-xs font-semibold tracking-[0.2em] text-site-muted uppercase">{date.weekday}</span>
                      <span className="baze-heading text-5xl leading-none tabular-nums">{String(date.day).padStart(2, "0")}</span>
                      <span className="text-xs font-semibold tracking-[0.2em] text-site-muted uppercase">{date.monthName}</span>
                    </time>
                  ) : (
                    <span aria-hidden="true" className="baze-heading text-5xl leading-none text-site-muted/50 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  )}
                  <div className="min-w-0">
                    <h3 className="baze-heading text-2xl leading-snug" {...field(ctx, `content.items.${i}.name`)}>
                      {item.name}
                    </h3>
                    <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mt-2 leading-relaxed text-site-muted" />
                  </div>
                  <div>
                    <Details ctx={ctx} item={item} i={i} />
                    <Actions ctx={ctx} item={item} showCalendar={showCalendar} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "stacked") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container size="narrow">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
          <ul className={cx(STACK, "space-y-5")}>
            {items.map((item, i) => {
              const date = parseEventDate(item.date);
              return (
                <li key={i} className={cx("baze-reveal grid gap-6 rounded-site-lg border border-site-border bg-site-surface p-6 shadow-site-sm @xl:grid-cols-[auto_1fr] @xl:items-center @3xl:p-8", CARD_HOVER)}>
                  {date ? <DateBlock date={date} large /> : null}
                  <div>
                    <h3 className="baze-heading text-2xl leading-snug @3xl:text-[1.75rem]" {...field(ctx, `content.items.${i}.name`)}>
                      {item.name}
                    </h3>
                    <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mt-2 mb-4 leading-relaxed text-site-muted" />
                    <div className={item.description ? undefined : "mt-4"}>
                      <Details ctx={ctx} item={item} i={i} />
                    </div>
                    <Actions ctx={ctx} item={item} showCalendar={showCalendar} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
        <ul className={cx(STACK, STAGGER, "grid gap-5 @3xl:gap-6", items.length === 1 ? "mx-auto max-w-xl" : items.length === 2 ? "mx-auto max-w-4xl @2xl:grid-cols-2" : "@2xl:grid-cols-2 @4xl:grid-cols-3")}>
          {items.map((item, i) => {
            const date = parseEventDate(item.date);
            return (
              <li key={i} className={cx("relative flex flex-col overflow-hidden rounded-site-lg border border-site-border bg-site-surface p-6 text-center shadow-site-sm @3xl:p-8", CARD_HOVER)}>
                <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-linear-to-r from-site-secondary via-site-primary to-site-secondary" />
                <p className="text-xs font-semibold tracking-[0.2em] text-site-primary uppercase">Acara {String(i + 1).padStart(2, "0")}</p>
                <h3 className="baze-heading mt-3 text-[1.75rem] leading-tight" {...field(ctx, `content.items.${i}.name`)}>
                  {item.name}
                </h3>
                {date ? (
                  <div className="my-6 flex items-center justify-center gap-4">
                    <span className="h-px flex-1 bg-site-border" aria-hidden="true" />
                    <DateBlock date={date} />
                    <span className="h-px flex-1 bg-site-border" aria-hidden="true" />
                  </div>
                ) : (
                  <div className="h-5" />
                )}
                <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mb-5 leading-relaxed text-site-muted" />
                <div className="mx-auto text-left">
                  <Details ctx={ctx} item={item} i={i} />
                </div>
                <div className="mt-auto flex justify-center">
                  <Actions ctx={ctx} item={item} showCalendar={showCalendar} />
                </div>
              </li>
            );
          })}
        </ul>
        {items.length === 0 && ctx.editor ? <p className={cx(STACK, "rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted")}>Tambahkan acara.</p> : null}
      </Container>
    </SectionShell>
  );
}
